import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useSettingsFormState,
  useSettingsLoading,
} from "../store/settings-store";
import { SettingsFooter } from "../components/SettingsFooter";
import { ThemeSection } from "./sections/ThemeSection";
import { LanguageSection } from "./sections/LanguageSection";
import { useAppearanceDraft } from "@/store/use-ui-preferences-store";

export const AppearanceTab: React.FC = () => {
  const { t } = useTranslation();
  const {
    setHasUnsavedChanges,
    markAsSaved,
    registerDraftReset,
    unregisterDraftReset,
  } = useSettingsFormState("appearance");
  const { isLoading } = useSettingsLoading("appearance");
  const { hasDraftChanges, isSaving, saveDraftChanges, resetDraftChanges } =
    useAppearanceDraft();

  // Track draft changes for unsaved changes state
  useEffect(() => {
    setHasUnsavedChanges(hasDraftChanges);
  }, [hasDraftChanges, setHasUnsavedChanges]);

  // Register a draft resetter so global navigation can clear drafts
  useEffect(() => {
    registerDraftReset(resetDraftChanges);
    return () => unregisterDraftReset(resetDraftChanges);
  }, [registerDraftReset, unregisterDraftReset, resetDraftChanges]);

  const handleSave = async () => {
    if (hasDraftChanges) {
      await saveDraftChanges();
    }
    markAsSaved();
  };

  const handleCancel = () => {
    resetDraftChanges();
    markAsSaved();
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Section Header */}
      <div className="flex flex-col gap-5 pb-5 border-b border-border-secondary dark:border-border-secondary">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary leading-7">
            {t("settings.appearance.title")}
          </h2>
          <p className="text-sm text-text-tertiary dark:text-text-tertiary leading-5">
            {t("settings.appearance.description")}
          </p>
        </div>
      </div>

      {/* Theme Section */}
      <ThemeSection />

      {/* Divider */}
      <div className="h-px bg-border-secondary dark:border-border-secondary" />

      {/* Language Section */}
      <LanguageSection />

      {/* Footer Actions */}
      <SettingsFooter
        onSave={handleSave}
        onCancel={handleCancel}
        isLoading={isLoading || isSaving}
        disabled={!hasDraftChanges}
      />
    </div>
  );
};
